/**
 * Contains functions for making requests to the water billing backend API endpoints.
 * Using Axios HTTP client — mirrors the structure of the tenants api.js.
 */

import axios from 'axios';
import auth from '../Auth/Auth';
import BASE_URL from '../config';

const API_BASE_URL = BASE_URL;

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach authentication token to all requests
api.interceptors.request.use(
  (config) => {
    const token = auth.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for consistent error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const errorMessage = error.response?.data?.error || error.message;
    console.error('Water Bills API Error:', errorMessage);
    return Promise.reject(new Error(errorMessage));
  }
);

// =============================================================================
// WATER UNITS
// =============================================================================

/**
 * Get all units with their water data, grouped by apartment → category → units.
 *
 * Response shape:
 * {
 *   apartments: [{ id, name, categories: [{ id, name, units: [...waterRow] }] }],
 *   globalRate: 80.0
 * }
 *
 * @returns {Promise<{ apartments: Array, globalRate: number }>}
 */
export const getWaterUnits = async () => {
  try {
    return await api.get('/tenants/water/units/');
  } catch (error) {
    console.error('❌ Error fetching water units:', error.message);
    throw error;
  }
};

/**
 * Undo the most recent water reading for an occupied unit.
 *
 * What it does:
 *   - Reverses the bill: +billAmount back to tenant balance
 *   - Restores water_balance in BalanceBreakdown
 *   - Resets reading.currentReading = previousReading (consumption = 0, bill = 0)
 *   - Syncs UnitWaterMeter back to previousReading
 *   - Creates a reversal Transaction for audit
 *
 * After undo, currReading === prevReading — the Edit dialog will unlock,
 * allowing a corrected recording to be entered.
 *
 * @param {number} unitId - ID of the unit to undo
 * @returns {Promise<{
 *   ok: boolean,
 *   unitId: number,
 *   prevReading: number,
 *   currReading: number,   // equals prevReading after undo
 *   consumption: number,   // 0
 *   billAmount: number,    // 0
 *   reversedAmount: number,
 *   arrears: number,       // remaining arrears after reversal
 *   newBalance: number
 * }>}
 */
export const undoWaterReading = async (unitId) => {
  try {
    return await api.post(`/tenants/water/units/${unitId}/undo/`);
  } catch (error) {
    console.error(`❌ Error undoing water reading for unit ${unitId}:`, error.message);
    throw error;
  }
};

/**
 * Correct a single unit's water reading.
 *
 * For OCCUPIED units:
 *   The reading must have been undone first (currReading === prevReading).
 *   If not, the backend returns 400.
 *
 *   payload: {
 *     prevReading: number,
 *     currReading: number,
 *     rate: number,
 *     imposeOnBalance: boolean   // default true — applies bill to tenant balance
 *   }
 *
 *   imposeOnBalance = true  → updates reading + creates Transaction + adjusts balance/breakdown
 *   imposeOnBalance = false → updates reading fields only, no financial impact
 *
 * For VACANT units:
 *   payload: { currReading?: number, rate?: number }
 *   → Updates UnitWaterMeter only; no tenant balance affected.
 *
 * @param {number} unitId
 * @param {{
 *   prevReading?: number,
 *   currReading?: number,
 *   rate?: number,
 *   imposeOnBalance?: boolean
 * }} payload
 * @returns {Promise<{
 *   ok: boolean,
 *   unitId: number,
 *   prevReading: number,
 *   currReading: number,
 *   rate: number,
 *   consumption: number,
 *   billAmount: number,
 *   arrears: number,
 *   imposed: boolean
 * }>}
 */
export const editWaterUnit = async (unitId, payload) => {
  try {
    return await api.patch(`/tenants/water/units/${unitId}/`, payload);
  } catch (error) {
    console.error(`❌ Error editing water unit ${unitId}:`, error.message);
    throw error;
  }
};

// =============================================================================
// WATER RATES
// =============================================================================

/**
 * Get the current water rate summary — global rate plus per-apartment/category rates.
 *
 * Response shape:
 * {
 *   globalRate: 80.0,
 *   apartments: [{ id, name, categories: [{ id, name, rate }] }]
 * }
 *
 * @returns {Promise<{ globalRate: number, apartments: Array }>}
 */
export const getWaterRates = async () => {
  try {
    return await api.get('/tenants/water/rates/');
  } catch (error) {
    console.error('❌ Error fetching water rates:', error.message);
    throw error;
  }
};

/**
 * Update water rates — either globally or for a specific apartment + category.
 *
 * Global mode:
 *   payload: { mode: 'global', rate: number }
 *   → Sets rate on every UnitWaterMeter and every active tenant's latest WaterReading.
 *
 * Category mode:
 *   payload: { mode: 'category', apartmentId: number, categoryId: number, rate: number }
 *   → Sets rate only for units in the specified apartment + category.
 *
 * @param {{ mode: 'global'|'category', rate: number, apartmentId?: number, categoryId?: number }} payload
 * @returns {Promise<{ ok: boolean, updatedUnits: number, newRate: number }>}
 */
export const updateWaterRates = async (payload) => {
  try {
    return await api.patch('/tenants/water/rates/', payload);
  } catch (error) {
    console.error('❌ Error updating water rates:', error.message);
    throw error;
  }
};

export default api;